import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  DollarSign,
  Shield,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  TrendingUp,
} from 'lucide-react';
import type { InsuranceProduct } from '@/data/insurance-products';
import type { ProductComparison } from '@/services/product-recommendations';

interface ProductRecommendationsPanelProps {
  products: InsuranceProduct[];
  comparisons?: ProductComparison[];
  responseMode?: 'no_action' | 'recommend' | 'compare';
  isPracticeStarted: boolean;
  isUpdating: boolean;
  language: 'en' | 'ko' | 'ja';
}

export function ProductRecommendationsPanel({
  products,
  comparisons = [],
  responseMode = 'no_action',
  isPracticeStarted,
  isUpdating,
  language,
}: ProductRecommendationsPanelProps) {
  const { t } = useTranslation();
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);

  const isKorean = language === 'ko';
  const isJapanese = language === 'ja';
  console.log('---products', products);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="h-full bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col shadow-sm"
    >
      {/* Header - Clean White Sticky */}
      <div className="px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
        <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center text-purple-600">
            <Sparkles className="w-4 h-4" />
          </div>
          {t('insurance.recommendedProducts')}
        </h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 relative bg-gray-50/30">
        {/* Compact loading spinner overlay - only show when updating with existing products */}
        <AnimatePresence>
          {isUpdating && isPracticeStarted && products.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="mb-3"
            >
              <div className="bg-white border border-purple-100 rounded-lg px-3 py-2 flex items-center gap-2 shadow-sm">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Sparkles className="w-4 h-4 text-purple-600" />
                </motion.div>
                <span className="text-xs font-medium text-purple-700">
                  {isKorean
                    ? '상품 업데이트 중...'
                    : isJapanese
                      ? '商品を更新中...'
                      : 'Updating recommendations...'}
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main content area */}
        {/* Show centered loading only for VERY FIRST time (no products exist and never existed) */}
        {!isPracticeStarted ||
        (isUpdating && products.length === 0 && !isPracticeStarted) ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-gray-400 px-4">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-sm font-medium mb-1 text-gray-600">
                {t('insurance.products.emptyStateTitle')}
              </p>
              <p className="text-xs text-gray-400 max-w-[200px] mx-auto">
                {t('insurance.products.emptyStateMessage')}
              </p>
            </div>
          </div>
        ) : isUpdating && products.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="inline-block mb-4"
              >
                <div className="w-12 h-12 rounded-full border-2 border-purple-100 border-t-purple-600" />
              </motion.div>
              <p className="text-sm font-medium mb-1 text-gray-900">
                {isKorean ? '분석 중...' : isJapanese ? '分析中...' : 'Analyzing...'}
              </p>
              <p className="text-xs text-gray-500">
                {isKorean ? '대화 내용 기반 상품 추천' : isJapanese ? '会話内容に基づく商品推薦' : 'Finding best matches'}
              </p>
            </div>
          </div>
        ) : products.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-gray-400 px-4">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-sm font-medium mb-1 text-gray-600">
                {t('insurance.products.emptyStateTitle')}
              </p>
              <p className="text-xs text-gray-400">
                {t('insurance.products.emptyStateMessage')}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence mode="popLayout">
              {products.slice(0, 5).map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  rank={index + 1}
                  isExpanded={expandedProduct === product.id}
                  onToggle={() =>
                    setExpandedProduct(
                      expandedProduct === product.id ? null : product.id,
                    )
                  }
                  language={language}
                  isKorean={isKorean}
                  isJapanese={isJapanese}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
}

interface ProductCardProps {
  product: InsuranceProduct;
  rank: number;
  isExpanded: boolean;
  onToggle: () => void;
  language: string;
  isKorean: boolean;
  isJapanese: boolean;
}

function ProductCard({
  product,
  rank,
  isExpanded,
  onToggle,
  isKorean,
  isJapanese,
}: ProductCardProps) {
  const relevancePercentage = Math.round((product.relevanceScore || 0) * 100);

  // Determine confidence level color
  const getConfidenceColor = (score: number) => {
    if (score >= 90) return 'text-green-700 bg-green-50 border-green-100';
    if (score >= 75) return 'text-blue-700 bg-blue-50 border-blue-100';
    if (score >= 60) return 'text-yellow-700 bg-yellow-50 border-yellow-100';
    return 'text-gray-700 bg-gray-50 border-gray-100';
  };

  const getConfidenceBadge = (score: number) => {
    if (score >= 90) return isKorean ? '매우 적합' : isJapanese ? '高マッチ' : 'High Match';
    if (score >= 75) return isKorean ? '적합' : isJapanese ? '良マッチ' : 'Good Match';
    if (score >= 60) return isKorean ? '보통' : isJapanese ? '中マッチ' : 'Medium';
    return isKorean ? '낮음' : isJapanese ? '低' : 'Low';
  };

  return (
    <motion.div
      layout="position"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -15 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className={`bg-white rounded-xl border transition-all duration-200 overflow-hidden ${
        isExpanded
          ? 'border-purple-200 shadow-md ring-1 ring-purple-50'
          : 'border-gray-200 hover:border-purple-200 hover:shadow-sm'
      }`}
    >
      <div
        className="p-4 cursor-pointer"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onToggle();
          }
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-gray-900 text-white text-xs font-bold shadow-sm">
                {rank}
              </span>
              <div
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${getConfidenceColor(
                  relevancePercentage,
                )}`}
              >
                <Sparkles className="w-2.5 h-2.5" />
                <span>{relevancePercentage}%</span>
              </div>
            </div>
            <h3 className="font-bold text-gray-900 text-sm leading-tight mb-0.5">
              {(isKorean || isJapanese) ? product.nameKo : product.name}
            </h3>
            <div className="text-xs text-gray-500 font-medium">
              {(isKorean || isJapanese) ? product.insurerKo : product.insurer}
            </div>
          </div>
          <div className="flex-shrink-0 pt-1">
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>

        {/* Match Reason - Enhanced with icon */}
        {product.matchReasonKo && (
          <div className="flex items-start gap-2 text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2 mb-3">
            <TrendingUp className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-purple-600" />
            <span className="leading-relaxed">
              {(isKorean || isJapanese) ? product.matchReasonKo : product.matchReason}
            </span>
          </div>
        )}

        {/* Quick Info */}
        {!isExpanded && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-white border border-gray-100 rounded-lg px-3 py-2">
              <div className="text-gray-400 font-medium mb-0.5 flex items-center gap-1 text-[10px] uppercase tracking-wider">
                {isKorean ? '월 보험료' : isJapanese ? '月額保険料' : 'Monthly'}
              </div>
              <div className="font-bold text-gray-900">
                {(isKorean || isJapanese)
                  ? product.pricing.baseMonthlyKo
                  : product.pricing.baseMonthly}
              </div>
            </div>
            <div className="bg-white border border-gray-100 rounded-lg px-3 py-2">
              <div className="text-gray-400 font-medium mb-0.5 flex items-center gap-1 text-[10px] uppercase tracking-wider">
                {isKorean ? '보장금액' : isJapanese ? '保障金額' : 'Coverage'}
              </div>
              <div className="font-bold text-gray-900">
                {(isKorean || isJapanese) ? product.coverage.amountKo : product.coverage.amount}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: 'easeInOut',
            }}
            className="border-t border-gray-100 bg-gray-50/50"
          >
            <div className="p-4 space-y-4">
              {/* Description */}
              <div>
                <div className="text-xs font-bold text-gray-900 mb-1.5">
                  {isKorean ? '상품 설명' : isJapanese ? '商品説明' : 'Description'}
                </div>
                <div className="text-xs text-gray-600 leading-relaxed bg-white p-3 rounded-lg border border-gray-100">
                  {(isKorean || isJapanese) ? product.descriptionKo : product.description}
                </div>
              </div>

              {/* Quick Info - Expanded view */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-white border border-gray-100 rounded-lg px-3 py-2">
                  <div className="text-gray-400 font-medium mb-0.5 flex items-center gap-1 text-[10px] uppercase tracking-wider">
                    {isKorean ? '월 보험료' : isJapanese ? '月額保険料' : 'Monthly'}
                  </div>
                  <div className="font-bold text-gray-900">
                    {isKorean
                      ? product.pricing.baseMonthlyKo
                      : product.pricing.baseMonthly}
                  </div>
                </div>
                <div className="bg-white border border-gray-100 rounded-lg px-3 py-2">
                  <div className="text-gray-400 font-medium mb-0.5 flex items-center gap-1 text-[10px] uppercase tracking-wider">
                    {isKorean ? '보장금액' : isJapanese ? '保障金額' : 'Coverage'}
                  </div>
                  <div className="font-bold text-gray-900">
                    {isKorean
                      ? product.coverage.amountKo
                      : product.coverage.amount}
                  </div>
                </div>
              </div>

              {/* Key Benefits - Why this product */}
              {product.keyBenefitsKo && product.keyBenefitsKo.length > 0 && (
                <div>
                  <div className="text-xs font-bold text-gray-900 mb-2 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                    {isKorean ? '추천 이유' : isJapanese ? 'おすすめの理由' : 'Why This Product'}
                  </div>
                  <ul className="space-y-1.5">
                    {((isKorean || isJapanese)
                      ? product.keyBenefitsKo
                      : product.keyBenefits || []
                    )
                      .slice(0, 3)
                      .map((benefit, idx) => (
                        <li
                          key={idx}
                          className="text-xs text-gray-700 flex items-start gap-2"
                        >
                          <div className="w-1 h-1 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                          <span className="leading-relaxed">{benefit}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              {/* Key Features - Top 3 only */}
              <div>
                <div className="text-xs font-bold text-gray-900 mb-2">
                  {isKorean ? '주요 특징' : isJapanese ? '主な特徴' : 'Key Features'}
                </div>
                <ul className="space-y-2">
                  {product.features.slice(0, 3).map((feature, idx) => (
                    <li
                      key={idx}
                      className="text-xs text-gray-600 bg-white p-2.5 rounded-lg border border-gray-100"
                    >
                      <strong className="block text-gray-900 mb-0.5">
                        {(isKorean || isJapanese) ? feature.titleKo : feature.title}
                      </strong>
                      <span className="leading-relaxed">
                        {(isKorean || isJapanese) ? feature.descriptionKo : feature.description}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
